import React, { useState } from 'react';
import { toast } from 'react-toastify';
import api from '../../api/api';
import { Card, Button, Input, Select, Label, Icon } from '../../components/ui';

const CLASSES = [
  { value: '', label: 'Toutes les classes' },
  { value: '1', label: 'Classe 1 - Capitaux' },
  { value: '2', label: 'Classe 2 - Immobilisations' },
  { value: '3', label: 'Classe 3 - Stocks' },
  { value: '4', label: 'Classe 4 - Tiers' },
  { value: '5', label: 'Classe 5 - Trésorerie' },
  { value: '6', label: 'Classe 6 - Charges' },
  { value: '7', label: 'Classe 7 - Produits' },
];

export default function TrialBalance() {
  const [periodKey, setPeriodKey] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (periodKey) params.periodKey = periodKey;
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;
      if (classFilter) params.class = classFilter;

      const res = await api.get('/accounting/trial-balance', { params });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (periodKey) params.set('periodKey', periodKey);
      if (fromDate) params.set('fromDate', fromDate);
      if (toDate) params.set('toDate', toDate);

      const res = await api.get(`/accounting/export/trial-balance?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `balance_${periodKey || 'periode'}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export Excel téléchargé');
    } catch (e) {
      toast.error('Erreur export');
    }
  };

  const exportPdf = async () => {
    try {
      const params = new URLSearchParams();
      if (periodKey) params.set('periodKey', periodKey);
      if (fromDate) params.set('fromDate', fromDate);
      if (toDate) params.set('toDate', toDate);
      const res = await api.get(`/accounting/export/trial-balance/pdf?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `balance_${periodKey || 'periode'}.pdf`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export PDF téléchargé');
    } catch { toast.error('Erreur export PDF'); }
  };

  const exportWord = async () => {
    try {
      const params = new URLSearchParams();
      if (periodKey) params.set('periodKey', periodKey);
      if (fromDate) params.set('fromDate', fromDate);
      if (toDate) params.set('toDate', toDate);
      const res = await api.get(`/accounting/export/trial-balance/word?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `balance_${periodKey || 'periode'}.docx`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export Word téléchargé');
    } catch { toast.error('Erreur export Word'); }
  };

  const formatCurrency = (v) => new Intl.NumberFormat('fr-TN', { style: 'currency', currency: 'TND' }).format(v || 0);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">
          <Icon name="IconScale" className="text-indigo-600 mr-3" />
          Balance
        </h1>
        <p className="text-sm text-gray-600 mt-1">Balance des comptes - Totaux débiteurs et créditeurs.</p>
      </div>

      <Card className="mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <Label className="text-xs text-gray-500">Période (YYYY-MM)</Label>
            <Input value={periodKey} onChange={(e) => setPeriodKey(e.target.value)} placeholder="2026-02" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Du</Label>
            <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Au</Label>
            <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Classe</Label>
            <Select value={classFilter} onChange={(e) => setClassFilter(e.target.value)}>
              {CLASSES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </Select>
          </div>
          <div className="flex items-end gap-2">
            <Button onClick={loadData} disabled={loading}>
              {loading ? 'Chargement...' : 'Consulter'}
            </Button>
            <Button onClick={exportExcel} variant="secondary">Excel</Button>
            <Button onClick={exportPdf} variant="secondary">PDF</Button>
            <Button onClick={exportWord} variant="secondary">Word</Button>
          </div>
        </div>
      </Card>

      {data && (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">Compte</th>
                  <th className="px-3 py-2 text-left text-xs text-gray-500 uppercase">Libellé</th>
                  <th className="px-3 py-2 text-center text-xs text-gray-500 uppercase">Cl</th>
                  <th className="px-3 py-2 text-right text-xs text-gray-500 uppercase">Total Débit</th>
                  <th className="px-3 py-2 text-right text-xs text-gray-500 uppercase">Total Crédit</th>
                  <th className="px-3 py-2 text-right text-xs text-gray-500 uppercase">Solde Débiteur</th>
                  <th className="px-3 py-2 text-right text-xs text-gray-500 uppercase">Solde Créditeur</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {data.accounts?.length === 0 ? (
                  <tr><td colSpan={7} className="text-center text-gray-500 py-8">Aucun compte</td></tr>
                ) : (
                  data.accounts?.filter((a) => a.linesCount > 0).map((a, i) => (
                    <tr key={a.code || i} className="hover:bg-gray-50">
                      <td className="px-3 py-2 text-sm font-mono">{a.code}</td>
                      <td className="px-3 py-2 text-sm">{a.label}</td>
                      <td className="px-3 py-2 text-sm text-center">{a.class}</td>
                      <td className="px-3 py-2 text-sm text-right">{a.totalDebit ? formatCurrency(a.totalDebit) : ''}</td>
                      <td className="px-3 py-2 text-sm text-right">{a.totalCredit ? formatCurrency(a.totalCredit) : ''}</td>
                      <td className="px-3 py-2 text-sm text-right">{a.balance > 0 ? formatCurrency(a.balance) : ''}</td>
                      <td className="px-3 py-2 text-sm text-right">{a.balance < 0 ? formatCurrency(Math.abs(a.balance)) : ''}</td>
                    </tr>
                  ))
                )}
              </tbody>
              {data.totals && (
                <tfoot>
                  <tr className="font-bold bg-indigo-50">
                    <td colSpan={3} className="px-3 py-2">TOTAUX ({data.totals.nbComptes} comptes)</td>
                    <td className="px-3 py-2 text-right">{formatCurrency(data.totals.debit)}</td>
                    <td className="px-3 py-2 text-right">{formatCurrency(data.totals.credit)}</td>
                    <td className="px-3 py-2 text-right">{formatCurrency(data.totals.soldeDebiteur)}</td>
                    <td className="px-3 py-2 text-right">{formatCurrency(data.totals.soldeCrediteur)}</td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
